import { useRef, useState } from "react";
import type { DesignElement, ElementPatch, NewElement } from "../types/configurator";

const HISTORY_LIMIT = 50;

function makeId(): string {
  return `el-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

export function useDesign() {
  const [elements, setElements] = useState<DesignElement[]>([]);
  const [past, setPast] = useState<DesignElement[][]>([]);
  const [future, setFuture] = useState<DesignElement[][]>([]);
  const current = useRef<DesignElement[]>([]);
  const snapshot = useRef<DesignElement[] | null>(null);

  function setNow(next: DesignElement[]) {
    current.current = next;
    setElements(next);
  }

  /** Discrete change: recorded in history. */
  function apply(fn: (els: DesignElement[]) => DesignElement[]) {
    const prev = current.current;
    setPast((p) => [...p.slice(-HISTORY_LIMIT + 1), prev]);
    setFuture([]);
    setNow(fn(prev));
  }

  /** Continuous change (drag, typing): recorded once the gesture ends. */
  function live(fn: (els: DesignElement[]) => DesignElement[]) {
    setNow(fn(current.current));
  }

  function beginGesture() {
    snapshot.current = current.current;
  }

  function endGesture() {
    const snap = snapshot.current;
    snapshot.current = null;
    if (snap && snap !== current.current) {
      setPast((p) => [...p.slice(-HISTORY_LIMIT + 1), snap]);
      setFuture([]);
    }
  }

  function undo() {
    if (!past.length) return;
    const prev = past[past.length - 1];
    setPast(past.slice(0, -1));
    setFuture((f) => [current.current, ...f]);
    setNow(prev);
  }

  function redo() {
    if (!future.length) return;
    const [next, ...rest] = future;
    setFuture(rest);
    setPast((p) => [...p, current.current]);
    setNow(next);
  }

  function reset() {
    setPast([]);
    setFuture([]);
    setNow([]);
  }

  const patchFn = (id: string, patch: ElementPatch) => (els: DesignElement[]) =>
    els.map((e) => (e.id === id ? ({ ...e, ...patch } as DesignElement) : e));

  function add(el: NewElement): string {
    const id = makeId();
    apply((els) => [...els, { ...el, id } as DesignElement]);
    return id;
  }

  function duplicate(id: string): string | null {
    const el = current.current.find((e) => e.id === id);
    if (!el) return null;
    const newId = makeId();
    apply((els) => [...els, { ...el, id: newId, x: Math.min(1, el.x + 0.06), y: Math.min(1, el.y + 0.06) }]);
    return newId;
  }

  function reorder(id: string, direction: "front" | "back") {
    apply((els) => {
      const el = els.find((e) => e.id === id);
      if (!el) return els;
      const rest = els.filter((e) => e.id !== id);
      return direction === "front" ? [...rest, el] : [el, ...rest];
    });
  }

  return {
    elements,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    add,
    duplicate,
    reorder,
    remove: (id: string) => apply((els) => els.filter((e) => e.id !== id)),
    updateLive: (id: string, patch: ElementPatch) => live(patchFn(id, patch)),
    updateCommit: (id: string, patch: ElementPatch) => apply(patchFn(id, patch)),
    beginGesture,
    endGesture,
    undo,
    redo,
    reset,
  };
}

export type Design = ReturnType<typeof useDesign>;
